import { useFormContext, type FieldValues, type Path } from 'react-hook-form'
import styles from './PhoneInput.module.scss'

interface PhoneInputProps<T extends FieldValues> {
  name: Path<T>
  label?: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
  readOnly?: boolean
  testId?: string
}

const formatPhone = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 10)

  if (digits.length === 0) return ''
  if (digits.length <= 3) return `(${digits}`
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)} ${digits.slice(6)}`
}

const stripPhone = (value: string): string => {
  return value.replace(/\D/g, '').slice(0, 10)
}

export const PhoneInput = <T extends FieldValues>({
  name,
  label,
  placeholder = '(000) 000 0000',
  required = false,
  disabled = false,
  readOnly = false,
  testId,
}: PhoneInputProps<T>) => {
  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<T>()

  const value = watch(name) as string
  const error = errors[name]
  const errorMessage = error?.message as string | undefined

  const containerClassNames = [styles.container, !label && styles.noLabel]
    .filter(Boolean)
    .join(' ')

  const inputClassNames = [styles.input, error && styles.inputError]
    .filter(Boolean)
    .join(' ')

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value
    const digits = stripPhone(rawValue)
    setValue(name, digits as T[typeof name], { shouldValidate: true })
  }

  // Register the field with validation
  register(name, {
    required: required ? 'This field is required' : false,
    validate: (val) => {
      if (!val && !required) return true
      const digits = stripPhone(val || '')
      if (digits.length < 9) return 'Phone number must be at least 9 digits'
      if (digits.length > 10) return 'Phone number must be at most 10 digits'
      return true
    },
  })

  return (
    <div className={containerClassNames}>
      {label && (
        <label htmlFor={name} className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}
      <input
        id={name}
        type="tel"
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        className={inputClassNames}
        value={formatPhone(value || '')}
        onChange={handleChange}
        data-testid={testId || `phone-input-${name}`}
      />
      {errorMessage && (
        <span
          className={styles.errorMessage}
          data-testid={testId ? `${testId}-error` : `phone-input-${name}-error`}
        >
          {errorMessage}
        </span>
      )}
    </div>
  )
}
